/* eslint-disable eqeqeq */
// @ts-nocheck
import { CFormInput, CSpinner, CFormSelect } from "@coreui/react";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import BoxWrapper from "../../../components/BoxWrapper";
import RichTextEditor from "../../../components/RichTextEditor";
import { useCookie } from "../../../hooks/useCookie";
import { useToast } from "../../../hooks/useToast";
import { general } from "../../../locales/general";
import apiService from "../../../service/apiService";
import { useAuth } from "../../../hooks/useAuth";
import * as yup from "yup";
import { verifyFileList } from "../../../helpers/general";
import { FormMode } from "../../../helpers/enums";
import UndoIcon from "../../../components/icons/UndoIcon";
import FileUploader from "../../../components/FileUploader";
import InfiniteScrollSelect from "../../../components/InfiniteScrollSelect";
import { useSelectedPlace } from "../../../hooks/place/useSelectedPlace";

const packItemSchema = {
  title: yup.string().required("Ce champ est obligatoire"),
  isSaved: yup.boolean().default(false),
  isDeleted: yup.boolean().default(false),
};

const isPackItemsEmpty = (items) => {
  return items.filter((items) => items.isDeleted === false).length <= 0;
};

export default function NewPack({ mode = FormMode.CREATE }) {
  const [formLoading, setFormLoading] = useState(false);
  const [description, setDescription] = useState("");
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const navigate = useNavigate();
  const params = useParams();

  const { isAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const schema = yup.object({
    title: yup.string().required("Ce champ est obligatoire"),
    image: yup.mixed().required("Ce champ est obligatoire"),
    shortDescription: yup.string().nullable(),
    description: yup.string().nullable(),
    price: yup
      .number()
      .positive()
      .required("Ce champ est obligatoire")
      .typeError("S'il vous plait, entrez un nombre valide")
      .transform((v) => (isNaN(v) ? undefined : v)),
    priceInEuro: yup
      .number()
      .positive()
      .nullable(true)
      .typeError()
      .transform((_, val) => (val ? Number(val) : null)),
    ...(isAdmin
      ? {
          placeId: yup
            .object()
            .required("Ce champ est obligatoire")
            .typeError("Ce champ est obligatoire"),
        }
      : {}),
    items: yup
      .array()
      .min(1, "Veuillez saisir au moins un élément")
      .of(yup.object().shape(packItemSchema)),

    taxiActive: yup.string().optional(),
    latitude: yup.number().when("taxiActive", {
      is: true,
      then: () =>
        yup
          .number()
          .required("Ce champ est obligatoire")
          .typeError("Ce champ est obligatoire"),
      otherwise: () =>
        yup
          .number()
          .nullable()
          .optional()
          .typeError("Ce champ est obligatoire")
          .transform((_, val) => {
            const result = val == Number(val) ? Number(val) : null;
            return result;
          }),
    }),
    longitude: yup.number().when("taxiActive", {
      is: true,
      then: () =>
        yup
          .number()
          .required("Ce champ est obligatoire")
          .typeError("Ce champ est obligatoire"),
      otherwise: () =>
        yup
          .number()
          .nullable(true)
          .optional()
          .typeError("Ce champ est obligatoire")
          .transform((_, val) => {
            const result = val == Number(val) ? Number(val) : null;
            return result;
          }),
    }),
    addressName: yup.number().when("taxiActive", {
      is: true,
      then: () =>
        yup
          .string()
          .required("Ce champ est obligatoire")
          .typeError("Ce champ est obligatoire"),
      otherwise: () =>
        yup
          .string()
          .nullable(true)
          .optional()
          .typeError("Ce champ est obligatoire"),
    }),
    // points: yup.number().default(5).optional(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control: formControl,
    setValue: setFormValue,
    watch: watchFormValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      items: [{ title: "", isSaved: false, isDeleted: false }],
    },
  });

  // console.log("🚀 ~ file: index.tsx:205 ~ NewPack ~ errors:", errors);

  const selectedPlace = watchFormValue("placeId");
  const [image, setImage] = useState(1);
  const {
    fields: packItemsFields,
    append: appendPackItemField,
    remove: removePackItemField,
    update: updatePackItemField,
  } = useFieldArray({
    control: formControl,
    name: "items",
  });

  const formPackItemsValue = watchFormValue("items");
  const taxiActiveValue = watchFormValue("taxiActive");

  const createNewPackMutation = useMutation(async (data) => {
    return await apiService.MakePostRequest("packs", data, token, true);
  });

  const updatePackMutation = useMutation(async (data) => {
    return await apiService.MakePutRequest(
      `packs/${params.id}?placeId=${selectedPlaceId}`,
      data,
      token,
      true,
    );
  });

  const updatePackItemMutation = useMutation(async ({ packItemId, data }) => {
    return await apiService.MakePutRequest(
      `packs/${params.id}/pack-item/${packItemId}?placeId=${selectedPlaceId}`,
      data,
      token,
    );
  });

  const createNewPackItemMutation = useMutation(async ({ packId, data }) => {
    return await apiService.MakePostRequest(
      `packs/${packId}/pack-item?placeId=${selectedPlaceId}`,
      data,
      token,
    );
  });

  const deletePackItemMutation = useMutation(async (packItemId) => {
    return await apiService.MakeDeleteRequest(
      `packs/${params.id}/pack-item/${packItemId}?placeId=${selectedPlaceId}`,
      token,
    );
  });

  const createPack = async (data) => {
    try {
      setFormLoading(true);
      const newPack = await createNewPackMutation.mutateAsync(data);
      const packItems = data.items.map(async (item, index) => {
        return await createNewPackItemMutation.mutateAsync({
          packId: newPack.id,
          data: item,
        });
      });
      await Promise.all(packItems);
      toastSuccess(general.fr.message.packCreated);
      navigate(`/packs`);
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  const updatePack = async (data) => {
    try {
      // console.log("🚀 ~ file: index.tsx:263 ~ updatePack ~ data:", data);
      setFormLoading(true);
      const updatedPack = await updatePackMutation.mutateAsync(data);
      const packItemsToDelete = data.items.filter(
        (packItem) => packItem.isDeleted === true && packItem.isSaved === true,
      );
      const packItemsToUpdate = data.items.filter(
        (packItem) => packItem.isSaved === true && packItem.isDeleted === false,
      );
      const packItemsToCreate = data.items.filter(
        (packItem) => packItem.isSaved === false,
      );
      const deletedPackItems = packItemsToDelete.map(async (item, index) => {
        return await deletePackItemMutation.mutateAsync(item.itemId);
      });
      const updatedPackItems = packItemsToUpdate.map(async (item, index) => {
        return await updatePackItemMutation.mutateAsync({
          packItemId: item.itemId,
          data: item,
        });
      });
      const newPackItems = packItemsToCreate.map(async (item, index) => {
        return await createNewPackItemMutation.mutateAsync({
          packId: updatedPack.id,
          data: item,
        });
      });
      await Promise.all(deletedPackItems);
      await Promise.all(updatedPackItems);
      await Promise.all(newPackItems);
      toastSuccess(general.fr.message.packCreated);
      navigate(`/packs`);
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  const onSubmit = async (dataArg) => {
    const data = { ...dataArg };
    try {
      if (isPackItemsEmpty(data.items)) return;
      const imageFile = verifyFileList(data.image);
      data.image = imageFile;
      data.description = description;
      data.placeId = isAdmin ? data.placeId.id : selectedPlaceId;
      // data.points = data.points;
      if (mode === FormMode.UPDATE) {
        updatePack(data);
      } else {
        createPack(data);
      }
    } catch (error) {
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  useEffect(() => {
    if (mode === FormMode.UPDATE && params?.id) {
      // get the data for the pack
      const formFields = Object.keys(schema.fields);
      apiService.MakeGetRequest(`packs/${params.id}`).then((response) => {
        setDescription(response?.description);
        setFormValue("placeId", response.place);
        setImage(response?.image);
        // setFormValue("points", response?.points);
        Object.entries(response).forEach((entry) => {
          if (formFields.indexOf(entry[0]) !== -1) {
            if (entry[0] === "items") {
              const formatedItems = entry[1].map((item) => ({
                ...item,
                itemId: item.id,
                isSaved: true,
                isDeleted: false,
              }));
              setFormValue("items", formatedItems);
            } else {
              setFormValue(entry[0], entry[1]);
            }
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deletePackItem = async (item, index) => {
    if (item.isSaved === true) {
      updatePackItemField(index, { ...item, isDeleted: true });
    } else {
      removePackItemField(index);
    }
  };

  return (
    <>
      <div className=" mt-4 ">
        <BoxWrapper>
          <section className="p-4">
            <div className="border-bottom border-success">
              <h4>
                {mode === FormMode.UPDATE
                  ? "Mise à jour du pack"
                  : "Ajouter un pack"}
              </h4>
            </div>
            <form className="row form mt-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="col-md-6 mt-4">
                <label htmlFor="title" className="d-block">
                  Titre du Pack
                  <span className="text-md text-red">*</span>
                </label>
                <CFormInput
                  type="text"
                  className="custom-input"
                  placeholder="Titre du Pack"
                  id="title"
                  name="title"
                  {...register("title")}
                />
                {errors.title?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.title.message}
                  </div>
                )}
              </div>
              <div className="col-md-6 mt-4">
                <label htmlFor="image" className="d-block mb-1">
                  Image <span className="text-md text-red">*</span>
                </label>
                {/* {mode === FormMode.UPDATE && getFormValue("image") ? (
                  <ImagePreview url={getFormValue("image")} />
                ) : null} */}
                <FileUploader
                  type="file"
                  id="image"
                  name="image"
                  // className="custom-input"
                  accept="image/png,image/jpeg,image/jpg"
                  required={mode === FormMode.UPDATE ? false : true}
                  {...register("image")}
                  file={image}
                />
                {errors.image?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.image.message}
                  </div>
                )}
              </div>
              <div className="col-md-6 mt-4">
                <label htmlFor="shortDescription" className="d-block">
                  Brève description
                </label>
                <CFormInput
                  type="text"
                  className="custom-input"
                  placeholder="Brève description"
                  id="shortDescription"
                  name="shortDescription"
                  {...register("shortDescription")}
                />
                {errors.shortDescription?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.shortDescription.message}
                  </div>
                )}
              </div>
              <div className="col-md-6 mt-4">
                <label htmlFor="description" className="d-block">
                  Description
                </label>
                <RichTextEditor
                  defaultValue={description}
                  onChange={(data) => setDescription(data)}
                />
              </div>
              <div className="col-md-6 mt-4">
                <label htmlFor="price" className="d-block">
                  Prix <span className="text-md text-red">*</span>
                </label>
                <CFormInput
                  type="number"
                  className="custom-input"
                  placeholder="Prix"
                  id="price"
                  name="price"
                  {...register("price")}
                />
                {errors.price?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.price.message}
                  </div>
                )}
              </div>
              <div className="col-md-6 mt-4">
                <label htmlFor="price" className="d-block">
                  Prix En Euro
                </label>
                <CFormInput
                  type="number"
                  className="custom-input"
                  placeholder="Prix"
                  id="priceInEuro"
                  name="priceInEuro"
                  {...register("priceInEuro")}
                />
                {errors.priceInEuro?.message && (
                  <div
                    className="text-red-500 text-opacity-50"
                    style={{ color: "red" }}
                  >
                    {errors.priceInEuro.message}
                  </div>
                )}
              </div>
              {isAdmin && (
                <div className="col-md-6 mt-4">
                  <label htmlFor="placeId" className="d-block mb-2">
                    Établissement <span className="text-md text-red">*</span>
                    {selectedPlace?.isVisible === false ? (
                      <span className="text-attention">
                        {" "}
                        (cet établissement est invisible)
                      </span>
                    ) : null}
                  </label>
                  <Controller
                    name="placeId"
                    control={formControl}
                    render={({ field }) => {
                      return (
                        <InfiniteScrollSelect
                          name="placeId"
                          id="placeId"
                          isClearable={false}
                          error={errors?.placeId?.message}
                          getOptionLabel={(option) => option?.name}
                          getOptionValue={(option) => option.id}
                          isOptionSelected={(option, selectedValue) => {
                            const isSelected =
                              option?.id === selectedValue?.[0]?.id;
                            return isSelected;
                          }}
                          url={{
                            path: "places",
                          }}
                          {...field}
                        />
                      );
                    }}
                  />
                  {errors.placeId?.message && (
                    <div
                      className="text-red-500 text-opacity-50"
                      style={{ color: "red" }}
                    >
                      {errors.placeId.message}
                    </div>
                  )}
                </div>
              )}

              {/* {isAdmin && (
                <div className="col-md-6 mt-4">
                  <label htmlFor="points" className="d-block mb-2">
                    Points
                    {/* <span className="text-sm text-red"> *</span> 
                  </label>
                  <CFormInput
                    type="number"
                    className="custom-input"
                    placeholder="Points"
                    id="points"
                    name="points"
                    {...register("points")}
                    step="0.01"
                    defaultValue={6}
                  />
                  {errors.points?.message && (
                    <div
                      className="text-red-500 text-opacity-50"
                      style={{ color: "red" }}
                    >
                      {errors.points.message}
                    </div>
                  )}
                </div>
              )} */}
              <section className="mt-5">
                <h5>Éléments du pack</h5>
                {isPackItemsEmpty(formPackItemsValue) === true ? (
                  <div className="text-red text-opacity-50 mt-5">
                    Le pack doit contenir au moins un élément
                  </div>
                ) : null}
              </section>
              {packItemsFields.map((packItem, index) => {
                return (
                  <div
                    key={packItem?.id}
                    className="mt-4 d-flex justify-items-center align-items-end"
                  >
                    <div
                      className="d-flex justify-items-center align-items-end"
                      style={{
                        opacity: packItem?.isDeleted ? 0.4 : 1,
                        pointerEvents: packItem?.isDeleted ? "none" : "all",
                      }}
                    >
                      <div
                        className="me-3"
                        style={{
                          minWidth: "90px",
                        }}
                      >
                        Élément {index + 1}:{" "}
                      </div>
                      <div className="flex-1">
                        <label htmlFor="title" className="d-block">
                          Titre <span className="text-md text-red">*</span>
                        </label>
                        <CFormInput
                          type="text"
                          className="custom-input"
                          placeholder="Titre"
                          id="title"
                          name="title"
                          {...register(`items.${index}.title`, {
                            required: "Ce champ est obligatoire",
                          })}
                        />
                        {errors.items &&
                          errors.items[index] &&
                          !!errors.items[index].title &&
                          errors.items[index].title.message && (
                            <div style={{ color: "red" }}>
                              {errors.items[index].title.message}
                            </div>
                          )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger green-shadow w-20 text-white ms-3"
                        onClick={() => deletePackItem(packItem, index)}
                      >
                        Supprimer
                      </button>
                    </div>
                    {packItem.isDeleted ? (
                      <button
                        type="button"
                        className="btn btn-danger green-shadow w-20 text-white ms-3"
                        onClick={() =>
                          updatePackItemField(index, {
                            ...packItem,
                            isDeleted: false,
                          })
                        }
                      >
                        <UndoIcon />
                      </button>
                    ) : null}
                  </div>
                );
              })}
              <section className="mt-5">
                <button
                  type="button"
                  className="btn btn-info green-shadow w-20 text-white"
                  onClick={() => {
                    appendPackItemField({
                      title: "",
                      isSaved: false,
                      isDeleted: false,
                    });
                  }}
                >
                  Ajouter un élément de pack
                </button>
              </section>

              {isAdmin && (
                <section className="mt-5 border pt-3 rounded-sm">
                  <div className="">
                    <h5>Taxi Configuration</h5>
                  </div>
                  <div className="d-flex flex-wrap row form my-4">
                    <div className="col-md-6">
                      <label htmlFor="taxiActive" className="d-block">
                        Active
                        <span className="text-sm text-red">*</span>
                      </label>
                      <CFormSelect id="taxiActive" {...register("taxiActive")}>
                        <option value="true">Oui</option>
                        <option selected value="false">
                          Non
                        </option>
                      </CFormSelect>
                      {errors.taxiActive?.message && (
                        <div
                          className="text-red-500 text-opacity-50"
                          style={{ color: "red" }}
                        >
                          {errors.taxiActive.message}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="addressName" className="d-block">
                        Address
                        {taxiActiveValue === "true" && (
                          <span className="text-sm text-red">*</span>
                        )}
                      </label>
                      <input
                        type="text"
                        className="custom-input"
                        placeholder="Address"
                        id="addressName"
                        {...register("addressName")}
                        step="0.01"
                      />
                      {errors.addressName?.message && (
                        <div
                          className="text-red-500 text-opacity-50"
                          style={{ color: "red" }}
                        >
                          {errors.addressName.message}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="latitude" className="d-block">
                        Latutide
                        {taxiActiveValue === "true" && (
                          <span className="text-sm text-red">*</span>
                        )}
                      </label>
                      <input
                        type="number"
                        className="custom-input"
                        placeholder="latitude"
                        id="latitude"
                        {...register("latitude")}
                        step="0.01"
                      />
                      {errors.latitude?.message && (
                        <div
                          className="text-red-500 text-opacity-50"
                          style={{ color: "red" }}
                        >
                          {errors.latitude.message}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="longitude" className="d-block">
                        Longitude
                        {taxiActiveValue === "true" && (
                          <span className="text-sm text-red">*</span>
                        )}
                      </label>
                      <input
                        type="number"
                        className="custom-input"
                        placeholder="longitude"
                        id="longitude"
                        {...register("longitude")}
                        step="0.01"
                      />
                      {errors.longitude?.message && (
                        <div
                          className="text-red-500 text-opacity-50"
                          style={{ color: "red" }}
                        >
                          {errors.longitude.message}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}
              <section className="d-flex justify-content-center buttons gap-4 mt-5">
                <Link
                  to="/packs"
                  className="btn btn-danger red-shadow w-20 px-4 py-2 text-white"
                >
                  Annuler
                </Link>
                <button
                  className="btn btn-success green-shadow w-20 text-white"
                  type="submit"
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <div className="text-center">
                      <CSpinner size="sm" />
                    </div>
                  ) : (
                    "Enregistrer"
                  )}
                </button>
              </section>
            </form>
          </section>
        </BoxWrapper>
      </div>
    </>
  );
}
